import { useState } from "react";
import { toast } from "sonner";
import { Mail, ArrowRight, Phone, MessageCircle } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { SOCIALS } from "@/data/socials";

const CATEGORIES = ["Course enquiries", "Student support", "Market analysis", "Partnerships", "College programmes", "Media", "Careers", "General enquiries"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", category: CATEGORIES[0], message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setForm({ name: "", email: "", category: CATEGORIES[0], message: "" });
    } catch (err) { toast.error(apiErr(err)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page">
      <PageHero eyebrow="Contact" title="Get in Touch" subtitle="Course enquiries, student support, market analysis, partnerships, college programmes, media and careers." crumbs={[{ label: "Contact" }]} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-1 space-y-6">
          <div className="border border-border rounded-lg p-6">
            <Mail className="h-5 w-5 text-gold" />
            <h3 className="font-display font-semibold text-navy mt-3">Email</h3>
            <ul className="mt-2 space-y-1.5">
              {[
                ["Official", "contact@krvprime.com"],
                ["Support", "support@krvprime.com"],
                ["Enquiry", "enquiry@krvprime.com"],
                ["Info", "info@krvprime.com"],
                ["Admin", "admin@krvprime.com"],
                ["Business", "business@krvprime.com"],
              ].map(([label, email]) => (
                <li key={email} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-slate-400">{label}</span>
                  <a href={`mailto:${email}`} data-testid={`contact-email-${label.toLowerCase()}`} className="text-slate-600 hover:text-gold transition-colors">{email}</a>
                </li>
              ))}
            </ul>
          </div>
          <div className="border border-border rounded-lg p-6">
            <Phone className="h-5 w-5 text-gold" />
            <h3 className="font-display font-semibold text-navy mt-3">Phone</h3>
            <a href="tel:+919000004109" data-testid="contact-phone" className="block text-sm text-slate-600 hover:text-gold transition-colors mt-1">+91 90000 04109</a>
            <a
              href="https://wa.me/919000004109?text=Hi%20KRV Prime%2C%20I%27d%20like%20to%20know%20more."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp"
              className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[#25D366] text-white text-sm font-semibold hover:brightness-95 transition-all"
            >
              <MessageCircle className="h-4 w-4" /> Prefer WhatsApp? Chat now
            </a>
          </div>
          <div className="border border-border rounded-lg p-6">
            <h3 className="font-display font-semibold text-navy">KRV Prime</h3>
            <p className="text-sm text-slate-500 mt-1">KRV Prime is an independent financial-market education and information platform.</p>
          </div>
          <div className="border border-border rounded-lg p-6">
            <h3 className="font-display font-semibold text-navy">Follow Us</h3>
            <p className="text-sm text-slate-500 mt-1">Stay updated with our latest market insights and education.</p>
            <div className="flex gap-3 mt-4">
              {SOCIALS.filter((s) => s.href).map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`contact-social-${s.label.toLowerCase()}`}
                  aria-label={s.label}
                  className="h-10 w-10 inline-flex items-center justify-center rounded-md border border-border text-slate-500 hover:text-gold hover:border-gold/50 transition-colors"
                >
                  <s.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>
        <form onSubmit={submit} className="lg:col-span-2 bg-white border border-border rounded-lg p-8 space-y-4" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <input required value={form.name} onChange={set("name")} data-testid="contact-name" placeholder="Your name" className="h-11 rounded-md border border-border px-3.5 text-sm outline-none focus:ring-2 focus:ring-gold" />
            <input required type="email" value={form.email} onChange={set("email")} data-testid="contact-email" placeholder="Email" className="h-11 rounded-md border border-border px-3.5 text-sm outline-none focus:ring-2 focus:ring-gold" />
          </div>
          <select value={form.category} onChange={set("category")} data-testid="contact-category" className="w-full h-11 rounded-md border border-border px-3.5 text-sm outline-none focus:ring-2 focus:ring-gold">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <textarea required rows={5} value={form.message} onChange={set("message")} data-testid="contact-message" placeholder="Your message" className="w-full rounded-md border border-border px-3.5 py-3 text-sm outline-none focus:ring-2 focus:ring-gold resize-none" />
          <button type="submit" disabled={loading} data-testid="contact-submit" className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-navy text-white font-semibold text-sm hover:bg-navy-light transition-colors disabled:opacity-60">
            {loading ? "Sending…" : "Send Message"} <ArrowRight className="h-4 w-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
